/**
 * @aiPurpose generate a cryptographically random password using
 *   `crypto.getRandomValues` and rejection sampling so picking a character
 *   from each pool is uniform (no modulo bias).
 * @aiInvariant the four character pools are pinned constants in this
 *   module — no excluded ambiguous character (`0`, `O`, `l`, `I`, `1`,
 *   `|`, backtick, backslash, single/double quote) ever appears in the
 *   output by construction. The result always contains at least one
 *   lower-case letter, one upper-case letter, one digit, and one symbol.
 * @aiInvariant length bounds are pinned to `PasswordSchema` in
 *   `packages/shared/src/schemas/users.ts` (`z.string().min(12).max(256)`)
 *   so every generated value is guaranteed to satisfy the wire contract
 *   that login/create/reset/change-password forms validate against.
 * @aiPii the produced value is a secret — never log it (no `console.log`,
 *   no Sentry breadcrumb, no error message that includes the value).
 */

const LOWER = 'abcdefghijkmnopqrstuvwxyz';
const UPPER = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
const DIGIT = '23456789';
const SYMBOL = '!@#$%^&*()_+-=[]{}';
const ALL = LOWER + UPPER + DIGIT + SYMBOL;
// INVARIANT: MIN_LENGTH/MAX_LENGTH must match `PasswordSchema` (min 12, max
// 256). Drift here would let the generator return a value the wire-level
// Zod validator rejects, breaking the "press button → save" contract.
const MIN_LENGTH = 12;
const MAX_LENGTH = 256;
const DEFAULT_LENGTH = 16;

function randomIndex(poolSize: number): number {
  // Rejection sampling: read a 32-bit unsigned integer and discard values
  // outside the largest multiple of poolSize that fits in 2^32. This makes
  // the modulo of the accepted value uniformly distributed across [0,
  // poolSize). Loop bound is statistical — the rejection probability is at
  // most poolSize/2^32 per draw which is negligibly small.
  const limit = Math.floor(0x1_0000_0000 / poolSize) * poolSize;
  const buf = new Uint32Array(1);
  for (;;) {
    crypto.getRandomValues(buf);
    if (buf[0]! < limit) {
      return buf[0]! % poolSize;
    }
  }
}

function pickFrom(pool: string): string {
  return pool.charAt(randomIndex(pool.length));
}

function shuffle(input: string[]): string[] {
  // Fisher–Yates with cryptographic random — preserves the "one from each
  // class" invariant while removing the positional pattern (lower at 0,
  // upper at 1, digit at 2, symbol at 3) that a naive concat would produce.
  const out = input.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = randomIndex(i + 1);
    [out[i], out[j]] = [out[j]!, out[i]!];
  }
  return out;
}

/**
 * @aiPurpose generate a password of the requested length using the four
 *   pinned character pools.
 * @aiInvariant `length` must be a finite integer in `[12, 256]` to match
 *   `PasswordSchema`; throws otherwise. Each of the four character classes
 *   is guaranteed to appear at least once.
 * @aiPii return value is a secret.
 * @throws RangeError when `length` is not an integer or is outside
 *   `[12, 256]` (covers NaN, Infinity, and fractional values that would
 *   otherwise short-circuit the length loop and yield a wire-invalid
 *   4-character output).
 */
export function generateSecurePassword(length: number = DEFAULT_LENGTH): string {
  if (!Number.isInteger(length) || length < MIN_LENGTH || length > MAX_LENGTH) {
    throw new RangeError(
      `generateSecurePassword: length must be an integer in [${MIN_LENGTH}, ${MAX_LENGTH}], got ${length}`,
    );
  }
  const required = [pickFrom(LOWER), pickFrom(UPPER), pickFrom(DIGIT), pickFrom(SYMBOL)];
  const remaining: string[] = [];
  for (let i = required.length; i < length; i++) {
    remaining.push(pickFrom(ALL));
  }
  return shuffle([...required, ...remaining]).join('');
}
